/**
 * Lightweight currency conversion for budget checks.
 *
 * Rates are indicative mid-market values expressed as "how many units of this
 * currency equal 1 USD". They are only used to compare a creator's rate against
 * a campaign budget that may be denominated in a different currency — they are
 * not intended for settlement.
 */
const unitsPerUsd: Record<string, number> = {
  USD: 1,
  EUR: 0.92,
  KES: 129,
  UGX: 3800,
  TZS: 2600,
  CDF: 2800,
  ETB: 120,
  NGN: 1500,
  GHS: 15.2,
  ZAR: 18.4,
  NAD: 18.4,
  LSL: 18.4,
  SZL: 18.4,
  XOF: 605,
  XAF: 605,
  RWF: 1400,
  ZMW: 26,
  MWK: 1750,
  MZN: 64,
  BWP: 13.6,
  EGP: 48,
  MAD: 9.9,
  TND: 3.1,
  DZD: 134,
  AOA: 900,
  BIF: 2900,
  CVE: 101,
  KMF: 460,
  DJF: 178,
  ERN: 15,
  GMD: 71,
  GNF: 8600,
  LRD: 190,
  LYD: 4.9,
  MGA: 4500,
  MRU: 40,
  MUR: 46,
  SCR: 13.5,
  SLE: 23,
  SOS: 571,
  SSP: 1300,
  SDG: 600,
  STN: 22.5,
  ZWL: 360,
};

/**
 * Convert `amount` from one currency to another using indicative rates.
 * Returns null when either currency is unknown, so callers can choose to
 * keep (rather than wrongly hide) rows they cannot compare.
 */
export function convertCurrency(
  amount: number,
  from: string,
  to: string,
): number | null {
  if (!Number.isFinite(amount)) return null;
  if (from === to) return amount;
  const fromRate = unitsPerUsd[from];
  const toRate = unitsPerUsd[to];
  if (!fromRate || !toRate) return null;
  return (amount / fromRate) * toRate;
}